import { useCallback, useState } from "react";
import { ClassFormatFilterComponent } from "./ClassFormatFilter";
import { PaymentOptionsFilterComponent } from "./PaymentOptionsFilter";
import { ProgramTagsFilterComponent } from "./ProgramTagsFilter";
import { ProgramDurationDaysFilterComponent } from "./ProgramDurationDaysFilter";
import { ProgramLevelFilterComponent } from "./ProgramLevelFilter";
import { ProgramTypeFilterComponent } from "./ProgramTypeFilter";
import { ProgramIndustryFilterComponent } from "./ProgramIndustryFilter";
import { useDreamSearchFilterContext } from "../DreamSearchFilterContext";
import { useMixpanel } from "@portal/app/_components/hooks/useMixpanel";
import difference from "lodash/difference";
import { genFilterAnalyticsMixpanelArgs } from "../../../utils/searchHelpers";
import { kebabCase } from "@portal/app/_common/lib/changeCase";
import pick from "lodash/pick";

export type FilterComponentProps<TValue> = {
  setValue: (newValue: number | string[] | null, changedValue?: string | number) => void;
  value: TValue;
  setHasOptions: React.Dispatch<React.SetStateAction<boolean>>;
};

type FilterComponentFunction<TFilterValue> = (
  props: FilterComponentProps<TFilterValue>
) => React.JSX.Element;

type FilterToFilterComponentRecord = {
  [KFilterName in keyof GQL.DreamSearchFilters]: FilterComponentFunction<
    NonNullable<GQL.DreamSearchFilters[KFilterName]>
  >;
};

export const disabledFilterTooltipTitle =
  "These options are filtered out. Clear filters to see all results.";

export const filterNameToLabelMap: Record<
  | "program_environment"
  | "program_vocation_in"
  | "program_payment_options_type_in"
  | "program_duration_days_lte"
  | "program_level_in"
  | "program_tags_in"
  | "program_vocation_industry",
  string
> = {
  program_environment: "Class format",
  program_vocation_in: "Program type",
  program_payment_options_type_in: "Available payment options",
  program_duration_days_lte: "Program duration",
  program_level_in: "Program level",
  program_tags_in: "More filters",
  program_vocation_industry: "Industry",
};

export const filterObjectByKeys = <Key extends string = string, Value extends string = string>(
  fullOptions: Record<Key, Value>,
  allowedOptions: Value[] | undefined
): Partial<Record<Key, Value>> =>
  allowedOptions ? pick(fullOptions, allowedOptions) : fullOptions;

const filterMap = {
  program_vocation_in: ProgramTypeFilterComponent,
  program_environment: ClassFormatFilterComponent,
  program_payment_options_type_in: PaymentOptionsFilterComponent,
  program_tags_in: ProgramTagsFilterComponent,
  program_duration_days_lte: ProgramDurationDaysFilterComponent,
  program_level_in: ProgramLevelFilterComponent,
  program_vocation_industry: ProgramIndustryFilterComponent,
} as const satisfies FilterToFilterComponentRecord;

export const FilterComponent = <TFilterName extends keyof typeof filterMap>({
  filterName,
  setHasOptions,
}: {
  filterName: TFilterName;
  setHasOptions: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const analytics = useMixpanel();
  const { filters, setFilters } = useDreamSearchFilterContext();

  const setValueFunction = useCallback(
    (newValue: GQL.DreamSearchFilters[TFilterName]) => {
      setFilters((prevFilters) => {
        const newFilters = { ...prevFilters, [filterName]: newValue };
        if (newValue === null) {
          delete newFilters[filterName];
        }
        const existingValue = prevFilters[filterName];
        if (existingValue !== newValue && newValue !== undefined && newValue !== null) {
          if (Array.isArray(newValue)) {
            const addedValues = difference(newValue, existingValue as []);
            if ((addedValues ?? []).length > 0) {
              addedValues.forEach((addedValue) => {
                const { event_name, properties } = genFilterAnalyticsMixpanelArgs(
                  filterName,
                  addedValue,
                  newFilters
                );
                void analytics.track(event_name, properties);
              });
            }
          } else {
            const { event_name, properties } = genFilterAnalyticsMixpanelArgs(
              filterName,
              newValue,
              newFilters
            );
            void analytics.track(event_name, properties);
          }
        }
        return newFilters;
      });
    },
    [setFilters, filterName, analytics]
  );

  return (
    <div data-testid={kebabCase(`${filterNameToLabelMap[filterName]}-filter`)}>
      {filterMap[filterName]?.({
        // @ts-expect-error Because `TFilterName extends` could be a union, every
        // property value could be every type, which TS isn't smart enough to narrow
        setValue: setValueFunction,

        // @ts-expect-error Because `TFilterName extends` could be a union, every
        // property value could be every type, which TS isn't smart enough to narrow
        value: filters[filterName] ?? [],
        setHasOptions: setHasOptions,
      })}
    </div>
  );
};

export const DreamSearchFilter = <FilterName extends keyof typeof filterMap>({
  filterName,
}: {
  filterName: FilterName;
}) => {
  const [hasOptions, setHasOptions] = useState(true);

  return hasOptions ? (
    <FilterComponent filterName={filterName} setHasOptions={setHasOptions} />
  ) : null;
};
